import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useShop } from '../contexts/ShopContext';
import { supabase } from '../lib/supabase';
import { CheckCircleIcon, ExclamationTriangleIcon, ChevronRightIcon, Cog6ToothIcon, ClockIcon, XCircleIcon, LinkSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ProviderConnectModal from './ProviderConnectModal';
import ProviderEditModal from './ProviderEditModal';
import ProviderOnboardingInvite from './ProviderOnboardingInvite';
import ProductSearchResults from './ProductSearchResults';
import { useProductSearch } from '../hooks/useProductSearch';
import { useRemovedProducts } from '../hooks/useRemovedProducts';
import { useSupplierProfiles } from '../hooks/useSupplierProfiles';
import SupplierProfileCard from './SupplierProfileCard';
import SupplierComparison from './SupplierComparison';

interface ProviderRow {
  id: string;
  name: string | null;
  provider_type: string | null;
  is_active: boolean | null;
}

interface MerchantProviderMapRow {
  provider_id: string | null;
  stock_provider: boolean | null;
  orders_provider: boolean | null;
}

const Providers: React.FC = () => {
  const { user } = useAuth();
  const { currentShopId } = useShop();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [connections, setConnections] = useState<Record<string, MerchantProviderMapRow>>({});
  const [stockCounts, setStockCounts] = useState<Record<string, number>>({});
  const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
  const [rejectedRequests, setRejectedRequests] = useState<Record<string, string | null>>({});
  const [showRejectionReason, setShowRejectionReason] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<{ providerId: string | null; providerName: string | null; providerType: string | null }>({ providerId: null, providerName: null, providerType: null });
  const [editModal, setEditModal] = useState<{ providerId: string | null; providerName: string | null; providerType: string | null }>({ providerId: null, providerName: null, providerType: null });
  const [showInviteProvider, setShowInviteProvider] = useState(false);

  const productSearch = useProductSearch(true);

  // Merchant-wide "removed from my store" list (merchant_config.business_rules.skip_list).
  // Surfaced here rather than inside a provider catalogue because it applies across every
  // supplier — and because an invisible block list is exactly how merchants ended up
  // unchecking products per-supplier and freezing their stock instead.
  const removedProducts = useRemovedProducts(currentShopId);
  // Per-provider feed health (provider_feed_health, refreshed every 15 min). A dead feed
  // used to keep serving its last known stock silently — JKN did exactly that from 9 Sept
  // and nobody could see it. RLS scopes these rows to this shop's providers.
  const [feedHealth, setFeedHealth] = useState<Record<string, { last_seen_at: string | null; is_stale: boolean; units_at_risk: number | null }>>({});
  const [showRemoved, setShowRemoved] = useState(false);
  // Supplier-declared terms (provider_profiles). Shown on every card, connected or not:
  // the point is that a merchant reads shipping time and cost BEFORE asking to connect.
  const { profiles: supplierProfiles } = useSupplierProfiles();
  const [showCompare, setShowCompare] = useState(false);
  // Unlink a supplier. Deliberately routed through the edge function rather than a plain
  // delete: removing the link alone strands stock. Any product whose last supplier was this
  // one drops out of the sync payload and freezes at its final quantity, purchasable with
  // nothing behind it. The function zeroes those first, then unlinks.
  const [unlinkTarget, setUnlinkTarget] = useState<{ id: string; name: string } | null>(null);
  const [unlinkPreview, setUnlinkPreview] = useState<{ products: number; units: number } | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  const openUnlink = async (id: string, name: string) => {
    setUnlinkTarget({ id, name });
    setUnlinkPreview(null);
    setUnlinkError(null);
    setUnlinkBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('merchant-unlink-provider', {
        body: { shop_id: currentShopId, provider_id: id, preview: true },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setUnlinkPreview({ products: data.products_losing_supply ?? 0, units: data.units ?? 0 });
    } catch (e: any) {
      setUnlinkError(e?.message || 'Could not work out what this would affect');
    } finally {
      setUnlinkBusy(false);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    try {
      const { data, error } = await supabase.functions.invoke('merchant-unlink-provider', {
        body: { shop_id: currentShopId, provider_id: unlinkTarget.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setUnlinkTarget(null);
      await handleConnectionSuccess(unlinkTarget.name);
    } catch (e: any) {
      setUnlinkError(e?.message || 'Unlink failed');
    } finally {
      setUnlinkBusy(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!user?.id) throw new Error('Not authenticated');
        if (!currentShopId) throw new Error('No shop selected');
        setAppUserId(currentShopId);

        // Fetch all available active providers. Named columns, never '*': the table also
        // holds each supplier's API key, which no client may read (column grants, monorepo
        // migration 20261010000002), so '*' is refused outright.
        const { data: providerRows, error: providerErr } = await supabase
          .from('providers')
          .select('id, name, provider_type, is_active')
          .eq('is_active', true);
        if (providerErr) throw providerErr;
        setProviders(providerRows || []);

        // Fetch connection mappings for this merchant
        const { data: mappingRows, error: mapErr } = await supabase
          .from('merchant_provider_map')
          .select('provider_id, stock_provider, orders_provider')
          .eq('merchant_id', currentShopId);
        if (mapErr) throw mapErr;

        const byProvider: Record<string, MerchantProviderMapRow> = {};
        (mappingRows || []).forEach((m) => {
          if (m.provider_id) byProvider[m.provider_id] = m as MerchantProviderMapRow;
        });
        setConnections(byProvider);

        // Metric: inventory items count per provider (best-effort)
        // Try with user_id + provider_id, then fallback to provider_id only
        const invCounts = await Promise.all(
          (providerRows || []).map(async (p) => {
            // Use helper view with merchant_id for accurate per-merchant counts
            try {
              const res = await (supabase as any)
                .from('product_inventory_merchant')
                .select('id', { count: 'exact' })
                .eq('provider_id', p.id)
                .eq('merchant_id', currentShopId)
                .limit(1);
              return { id: p.id, count: (res.count as number) ?? 0 };
            } catch (_) {
              return { id: p.id, count: 0 };
            }
          })
        );
        const countsMap: Record<string, number> = {};
        invCounts.forEach(({ id, count }) => (countsMap[id] = count));
        setStockCounts(countsMap);

        // Feed health per provider
        const { data: healthRows } = await supabase
          .from('provider_feed_health')
          .select('provider_id, last_seen_at, is_stale, units_at_risk');
        const healthMap: Record<string, any> = {};
        (healthRows || []).forEach((h: any) => { healthMap[h.provider_id] = h; });
        setFeedHealth(healthMap);

        // Fetch pending connection requests for this merchant
        const { data: pendingRows } = await supabase
          .from('connection_requests')
          .select('provider_id')
          .eq('merchant_id', currentShopId)
          .eq('status', 'pending');
        setPendingRequests(new Set((pendingRows || []).map((r) => r.provider_id)));

        // Fetch rejected connection requests for this merchant
        const { data: rejectedRows } = await supabase
          .from('connection_requests')
          .select('provider_id, rejection_reason')
          .eq('merchant_id', currentShopId)
          .eq('status', 'rejected');
        const rejMap: Record<string, string | null> = {};
        (rejectedRows || []).forEach((r) => {
          if (r.provider_id) rejMap[r.provider_id] = r.rejection_reason ?? null;
        });
        setRejectedRequests(rejMap);
      } catch (e: any) {
        console.error('Failed to load providers:', e);
        setError(e?.message || 'Failed to load providers');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [user]);

  const connectedIds = useMemo(() => new Set(Object.keys(connections)), [connections]);

  const handleOpenConnectModal = (providerId: string, providerName: string, providerType: string) => {
    // Open unified modal with provider type
    setActiveModal({ providerId, providerName, providerType });
  };

  const handleOpenEditModal = (providerId: string, providerName: string, providerType: string) => {
    // Open edit modal for existing connection
    setEditModal({ providerId, providerName, providerType });
  };

  const handleCloseModal = () => {
    setActiveModal({ providerId: null, providerName: null, providerType: null });
  };

  const handleCloseEditModal = () => {
    setEditModal({ providerId: null, providerName: null, providerType: null });
  };

  const handleConnectionSuccess = async (providerName: string) => {
    // Close modal
    handleCloseModal();

    // Show success message
    toast.success(`Connection request sent to ${providerName} - pending approval`);

    // Refresh connections list
    if (appUserId) {
      const { data: mappingRows, error: mapErr } = await supabase
        .from('merchant_provider_map')
        .select('provider_id, stock_provider, orders_provider')
        .eq('merchant_id', appUserId);

      if (!mapErr && mappingRows) {
        const byProvider: Record<string, MerchantProviderMapRow> = {};
        mappingRows.forEach((m) => {
          if (m.provider_id) byProvider[m.provider_id] = m as MerchantProviderMapRow;
        });
        setConnections(byProvider);
      }

      // Refresh inventory counts
      const invCounts = await Promise.all(
        providers.map(async (p) => {
          try {
            const res = await (supabase as any)
              .from('product_inventory_merchant')
              .select('id', { count: 'exact' })
              .eq('provider_id', p.id)
              .eq('merchant_id', appUserId)
              .limit(1);
            return { id: p.id, count: (res.count as number) ?? 0 };
          } catch (_) {
            return { id: p.id, count: 0 };
          }
        })
      );
      const countsMap: Record<string, number> = {};
      invCounts.forEach(({ id, count }) => (countsMap[id] = count));
      setStockCounts(countsMap);

      // Refresh pending requests
      const { data: pendingRows } = await supabase
        .from('connection_requests')
        .select('provider_id')
        .eq('merchant_id', appUserId)
        .eq('status', 'pending');
      setPendingRequests(new Set((pendingRows || []).map((r) => r.provider_id)));

      // Refresh rejected requests
      const { data: rejectedRows } = await supabase
        .from('connection_requests')
        .select('provider_id, rejection_reason')
        .eq('merchant_id', appUserId)
        .eq('status', 'rejected');
      const rejMap: Record<string, string | null> = {};
      (rejectedRows || []).forEach((r) => {
        if (r.provider_id) rejMap[r.provider_id] = r.rejection_reason ?? null;
      });
      setRejectedRequests(rejMap);
    }
  };

  const handleProviderClick = (providerId: string) => {
    // Navigate to merchant-facing provider catalog route
    // This route is protected by ProtectedRoute (merchant auth) and shows read-only catalog
    navigate(`/providers/${providerId}/catalog`);
  };

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">

        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading font-bold text-white text-2xl">Providers</h1>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowCompare(!showCompare)}
              aria-expanded={showCompare}
              className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-sm hover:bg-white/[0.08] transition-colors"
            >
              {showCompare ? 'Hide comparison' : 'Compare suppliers'}
            </button>
            <button
              onClick={() => setShowRemoved(!showRemoved)}
              className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-sm hover:bg-white/[0.08] transition-colors"
            >
              Removed products{removedProducts.loaded ? ` (${removedProducts.removedCount})` : ''}
            </button>
            <button
              onClick={() => setShowInviteProvider(!showInviteProvider)}
              className="px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/20 transition-colors"
            >
              {showInviteProvider ? 'Hide Invite' : 'Invite New Provider'}
            </button>
          </div>
        </div>

        {/* Product search — always visible above the providers list */}
        <div className="mb-5">
          <ProductSearchResults
            results={productSearch.results}
            loading={productSearch.loading}
            error={productSearch.error}
            filters={productSearch.filters}
            updateFilters={productSearch.updateFilters}
            resetFilters={productSearch.resetFilters}
            allCategories={productSearch.allCategories}
            allVendors={productSearch.allVendors}
            totalItems={productSearch.totalItems}
            hasActiveFilters={productSearch.hasActiveFilters}
            connectedProviderIds={connectedIds}
            hideEmptyState
          />
        </div>

        {/* Providers grid (hidden while a product search is active) */}
        {!productSearch.hasActiveFilters && <>

        {/* Supplier comparison: shipping time, cost and sync speed side by side */}
        {showCompare && !loading && !error && providers.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
            <SupplierComparison suppliers={providers} profiles={supplierProfiles} connectedIds={connectedIds} />
          </motion.div>
        )}

        {/* Removed products panel */}
        {showRemoved && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-5 glass-card rounded-2xl p-5"
          >
            <h3 className="text-base font-heading font-bold text-white mb-1">Removed from your store</h3>
            <p className="text-white/40 text-xs mb-4">
              FlowStock will not upload these products, from any supplier, and keeps their stock at 0.
              Restoring one lets it sync again on the next run.
            </p>

            {removedProducts.error && (
              <div className="text-red-400 text-xs mb-3">{removedProducts.error}</div>
            )}

            {!removedProducts.loaded ? (
              <p className="text-white/30 text-sm">Loading…</p>
            ) : removedProducts.removedCount === 0 ? (
              <p className="text-white/30 text-sm">
                Nothing removed yet. Use the bin icon on a product in a supplier's catalogue.
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto divide-y divide-white/[0.06]">
                {Array.from(removedProducts.removed).sort().map((sku) => (
                  <div key={sku} className="flex items-center justify-between py-2">
                    <span className="text-white/70 text-sm font-mono truncate">{sku}</span>
                    <button
                      onClick={async () => {
                        const ok = await removedProducts.restoreProduct(sku);
                        if (!ok) console.error('restore failed for', sku);
                      }}
                      disabled={removedProducts.saving}
                      className="ml-3 flex-shrink-0 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                    >
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Invite New Provider Panel */}
        {showInviteProvider && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-5 glass-card rounded-2xl p-5"
          >
            <h3 className="text-base font-heading font-bold text-white mb-3">Generate Provider Onboarding Invite</h3>
            <p className="text-white/40 text-xs mb-4">Create an invite code that a new provider must use to sign up on the platform.</p>
            <ProviderOnboardingInvite
              merchantId={currentShopId || undefined}
              onClose={() => setShowInviteProvider(false)}
            />
          </motion.div>
        )}

        {/* States */}
        {loading && (
          <div className="glass-card rounded-2xl p-8 text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-white/40 text-sm">Loading providers…</p>
          </div>
        )}

        {!loading && error && (
          <div className="glass-card rounded-2xl p-6 border-l-2 border-l-red-500">
            <div className="flex items-center text-red-400 text-sm gap-2">
              <ExclamationTriangleIcon className="w-5 h-5" /> {error}
            </div>
          </div>
        )}

        {!loading && !error && providers.length === 0 && (
          <div className="glass-card rounded-2xl p-8 text-center text-white/30">No providers available yet.</div>
        )}

        {!loading && !error && providers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((p, i) => {
              const isConnected = connectedIds.has(p.id);
              const isPending = pendingRequests.has(p.id);
              const isRejected = !isConnected && !isPending && p.id in rejectedRequests;
              const mapping = connections[p.id];
              const invCount = stockCounts[p.id] ?? 0;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  whileHover={{ y: -2, transition: { type: 'spring', stiffness: 400 } }}
                  className={`glass-card rounded-2xl p-5 cursor-pointer ${isConnected ? 'border-l-2 border-l-orange-500' : ''}`}
                  onClick={() => handleProviderClick(p.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-heading font-bold text-white text-base truncate">
                        {p.name || 'Unnamed Provider'}
                      </h3>
                      <span className={`flex-shrink-0 w-2 h-2 rounded-full ${p.is_active ? 'bg-emerald-400' : 'bg-white/20'}`} />
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                      {isConnected ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                            <CheckCircleIcon className="w-3.5 h-3.5" /> Connected
                          </span>
                          <button
                            onClick={() => handleOpenEditModal(p.id, p.name || 'Provider', p.provider_type || '')}
                            className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/[0.06] transition-colors"
                            title="Edit connection"
                          >
                            <Cog6ToothIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openUnlink(p.id, p.name || 'Provider')}
                            className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-white/[0.06] transition-colors"
                            title="Unlink this supplier"
                            aria-label={`Unlink ${p.name || 'provider'}`}
                          >
                            <LinkSlashIcon className="w-4 h-4" />
                          </button>
                        </>
                      ) : isPending ? (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs font-semibold">
                          <ClockIcon className="w-4 h-4 mr-1" /> Pending Approval
                        </span>
                      ) : isRejected ? (
                        <div className="relative">
                          <button
                            onClick={() => setShowRejectionReason(showRejectionReason === p.id ? null : p.id)}
                            className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors"
                          >
                            <XCircleIcon className="w-4 h-4 mr-1" /> Rejected
                          </button>
                          {showRejectionReason === p.id && (
                            <div className="absolute right-0 top-full mt-2 w-64 p-3 rounded-lg bg-black/90 border border-white/[0.08] text-white/70 text-xs shadow-lg z-50">
                              <p className="font-semibold text-red-400 mb-1">Rejection Reason</p>
                              <p>{rejectedRequests[p.id] || 'No reason provided'}</p>
                              <button
                                onClick={() => handleOpenConnectModal(p.id, p.name || 'Provider', p.provider_type || '')}
                                className="mt-2 px-3 py-1 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-xs font-semibold transition-colors"
                              >
                                Request Again
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleOpenConnectModal(p.id, p.name || 'Provider', p.provider_type || '')}
                          className="px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-xs font-semibold transition-colors"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <SupplierProfileCard profile={supplierProfiles[p.id]} variant="compact" />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex gap-4 text-xs text-white/35 items-center flex-wrap">
                      <span><span className="text-orange-400 font-heading font-bold text-lg mr-1">{invCount}</span>items</span>
                      {(() => {
                        const h = feedHealth[p.id];
                        if (!h) return null;
                        const last = h.last_seen_at ? new Date(h.last_seen_at) : null;
                        const ago = last
                          ? (() => {
                              const mins = Math.round((Date.now() - last.getTime()) / 60000);
                              if (mins < 60) return `${mins}m ago`;
                              const hrs = Math.round(mins / 60);
                              return hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
                            })()
                          : 'never';
                        return h.is_stale ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400"
                            title={`No new stock data since ${last ? last.toLocaleString() : 'ever'}. This supplier's stock is treated as 0 so it can't be oversold.`}
                          >
                            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                            Feed stale — last data {ago}
                          </span>
                        ) : (
                          <span className="text-white/35" title={last ? last.toLocaleString() : undefined}>
                            Synced {ago}
                          </span>
                        );
                      })()}
                      <span>Stock: <span className={mapping?.stock_provider ? 'text-emerald-400' : 'text-white/25'}>{mapping?.stock_provider ? 'Yes' : 'No'}</span></span>
                      <span>Orders: <span className={mapping?.orders_provider ? 'text-emerald-400' : 'text-white/25'}>{mapping?.orders_provider ? 'Yes' : 'No'}</span></span>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-white/20" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        </>}

      </main>

      {/* Unlink confirmation. The numbers come from the server, not a guess: they are the
          products whose LAST supplier is this one, which are the ones at risk of being
          stranded. The edge function zeroes them before removing the link. */}
      {unlinkTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card rounded-xl p-6 max-w-md mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">
              Unlink {unlinkTarget.name}?
            </h3>

            {unlinkBusy && !unlinkPreview && !unlinkError && (
              <p className="text-white/50 text-sm">Checking what this affects…</p>
            )}

            {unlinkError && (
              <p className="text-red-400 text-sm">{unlinkError}</p>
            )}

            {unlinkPreview && (
              <>
                <p className="text-white/70 text-sm">
                  FlowStock will stop buying from this supplier. Products another supplier
                  also carries keep syncing as normal.
                </p>
                {unlinkPreview.products > 0
                  ? (
                    <p className="text-white/70 text-sm">
                      <span className="text-white font-semibold">{unlinkPreview.products}</span>{' '}
                      product{unlinkPreview.products === 1 ? '' : 's'} have no other supplier.
                      Their stock ({unlinkPreview.units} pcs) is set to{' '}
                      <span className="text-white font-semibold">0</span> first, so nothing
                      is left selling with no stock behind it.
                    </p>
                  )
                  : (
                    <p className="text-white/70 text-sm">
                      Every product this supplier carries is also carried by someone else, so
                      nothing goes out of stock.
                    </p>
                  )}
                <p className="text-white/40 text-xs">
                  You can connect this supplier again later; your login details are kept.
                </p>
              </>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setUnlinkTarget(null)}
                disabled={unlinkBusy}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnlink}
                disabled={unlinkBusy || !unlinkPreview}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40"
              >
                {unlinkBusy && unlinkPreview ? 'Unlinking…' : 'Unlink supplier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Modal */}
      {activeModal.providerId && user?.id && appUserId && (
        <ProviderConnectModal
          providerId={activeModal.providerId}
          providerName={activeModal.providerName || 'Provider'}
          providerType={activeModal.providerType || ''}
          merchantId={appUserId}
          authUserId={user.id}
          profile={supplierProfiles[activeModal.providerId]}
          onClose={handleCloseModal}
          onSuccess={() => handleConnectionSuccess(activeModal.providerName || 'Provider')}
        />
      )}

      {/* Edit Modal */}
      {editModal.providerId && user?.id && appUserId && (
        <ProviderEditModal
          providerId={editModal.providerId}
          providerName={editModal.providerName || 'Provider'}
          providerType={editModal.providerType || ''}
          merchantId={appUserId}
          authUserId={user.id}
          onClose={handleCloseEditModal}
          onSuccess={() => handleConnectionSuccess(editModal.providerName || 'Provider')}
        />
      )}
    </div>
  );
};

export default Providers;

